import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import './PublisherSettings.css';

interface PublisherSettingsProps {
  publisherUsername: string;
}

interface PublisherData {
  id: number;
  name: string;
  email: string;
  username: string;
  api_key: string;
  callback_template: string;
  country: string;
  timezone: string;
  tier: string;
  status: string;
}

const PublisherSettings: React.FC<PublisherSettingsProps> = ({ publisherUsername }) => {
  const [publisherData, setPublisherData] = useState<PublisherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showApiKey, setShowApiKey] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3002';

  useEffect(() => {
    fetchPublisherData();
  }, [publisherUsername]);

  const fetchPublisherData = async () => {
    try {
      setLoading(true);
      const publisherResponse = await apiService.getPublisherMe();
      const publisher = publisherResponse.publisher;

      if (publisher) {
        setPublisherData({
          id: publisher.id,
          name: publisher.name,
          email: publisher.email,
          username: publisher.username,
          api_key: publisher.api_key,
          callback_template: publisher.callback_template,
          country: publisher.country,
          timezone: publisher.timezone,
          tier: publisher.tier,
          status: publisher.status
        });
      }
    } catch (err: any) {
      console.error('Error fetching publisher data:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    alert(`${label} copied to clipboard!`);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    try {
      setChangingPassword(true);

      const publisherToken = localStorage.getItem('publisherToken');
      const response = await fetch(`${apiBaseUrl}/api/publisher/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(publisherToken ? { Authorization: `Bearer ${publisherToken}` } : {})
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordMessage({ type: 'error', text: data.message || 'Failed to change password' });
      }
    } catch (err: any) {
      setPasswordMessage({ type: 'error', text: 'Error changing password. Please try again.' });
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <div className="publisher-settings-page"><div className="loading">Loading settings...</div></div>;
  }

  if (!publisherData) {
    return <div className="publisher-settings-page"><div className="error">Publisher not found</div></div>;
  }

  return (
    <div className="publisher-settings-page">
      <div className="page-header">
        <h1>Account Settings</h1>
        <p>Manage your account information and API credentials</p>
      </div>

      <div className="offers-section">
        <div className="section-header">
          <h2>Account Manager</h2>
        </div>
        <div className="table-container">
          <table className="offers-table">
            <tbody>
              <tr>
                <td style={{ width: '200px' }}><strong>Manager Name</strong></td>
                <td>Reema Sharma</td>
              </tr>
              <tr>
                <td><strong>Manager E-mail</strong></td>
                <td>
                  <a href="mailto:reema@adbizzmedia.com" className="settings-link">
                    reema@adbizzmedia.com
                  </a>
                </td>
              </tr>
              <tr>
                <td><strong>Manager Teams</strong></td>
                <td>
                  <a
                    href="https://teams.live.com/l/invite/FEA87IXVBmVvEbBDg4?v=g1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="settings-link"
                  >
                    Join Teams Meeting
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="offers-section">
        <div className="section-header">
          <h2>Account Information</h2>
        </div>
        <div className="table-container">
          <table className="offers-table">
            <tbody>
              <tr>
                <td className="settings-label-cell"><strong>Publisher Name</strong></td>
                <td>{publisherData.name}</td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>Email</strong></td>
                <td>{publisherData.email}</td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>Username</strong></td>
                <td>{publisherData.username}</td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>Country</strong></td>
                <td>{publisherData.country}</td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>Timezone</strong></td>
                <td>{publisherData.timezone}</td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>Account Status</strong></td>
                <td>
                  <span className={`status-badge status-${publisherData.status}`}>
                    {publisherData.status}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="offers-section">
        <div className="section-header">
          <h2>API Credentials</h2>
        </div>
        <div className="table-container">
          <table className="offers-table">
            <thead>
              <tr className="settings-credentials-head">
                <th style={{ width: '200px' }}>Credential</th>
                <th>Value</th>
                <th style={{ width: '150px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="settings-label-cell"><strong>Affiliate ID</strong></td>
                <td>
                  <code className="settings-code">
                    {publisherData.id}
                  </code>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => copyToClipboard(publisherData.id.toString(), 'Affiliate ID')}
                  >
                    Copy
                  </button>
                </td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>API Key</strong></td>
                <td>
                  <code className="settings-code">
                    {showApiKey ? publisherData.api_key : '••••••••••••••••••••••••••••••••'}
                  </code>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setShowApiKey(!showApiKey)}
                    style={{ marginRight: '5px' }}
                  >
                    {showApiKey ? 'Hide' : 'Show'}
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => copyToClipboard(publisherData.api_key, 'API Key')}
                  >
                    Copy
                  </button>
                </td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>API Base URL</strong></td>
                <td>
                  <code className="settings-code">
                    {apiBaseUrl}
                  </code>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => copyToClipboard(apiBaseUrl, 'API URL')}
                  >
                    Copy
                  </button>
                </td>
              </tr>
              <tr>
                <td className="settings-label-cell"><strong>Postback URL</strong></td>
                <td>
                  <code className="settings-code">
                    {publisherData.callback_template || 'Not configured'}
                  </code>
                </td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => copyToClipboard(publisherData.callback_template || 'Not configured', 'Postback URL')}
                    disabled={!publisherData.callback_template}
                  >
                    Copy
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="settings-info-box">
            <strong>API Documentation:</strong> Use these credentials to integrate with our API.
            For detailed documentation, please contact your account manager or support.
          </div>
        </div>
      </div>

      <div className="offers-section">
        <div className="section-header">
          <h2>Change Password</h2>
        </div>
        <div className="table-container">
          <form onSubmit={handlePasswordChange} className="settings-form">
            <div className="settings-field">
              <label className="settings-field-label">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="settings-input"
                required
              />
            </div>

            <div className="settings-field">
              <label className="settings-field-label">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="settings-input"
                required
                minLength={6}
              />
            </div>

            <div className="settings-field">
              <label className="settings-field-label">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="settings-input"
                required
                minLength={6}
              />
            </div>

            {passwordMessage && (
              <div className={`settings-message ${passwordMessage.type}`}>
                {passwordMessage.text}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={changingPassword}
            >
              {changingPassword ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublisherSettings;
